import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@ublend-npm/aulaui-core';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { QuickLink } from '../constants/quicklinkConstants';

interface QuickLinkTileProps {
  link: QuickLink;
  role: 'student' | 'educator';
  isCompact?: boolean;
}

const QuickLinkTile: React.FC<QuickLinkTileProps> = ({
  link,
  role,
  isCompact,
}) => {
  const { label, circleBg, icon, href, iconSize } = link;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hrefKey = role === 'student' ? 'learner' : 'educator';
  const hrefByRole = href[hrefKey];
  const [isHovered, setIsHovered] = useState(false);

  const tileBg = theme.palette.background.default;
  const tileBgHover = isDark ? '#2C2733' : '#E4E1EA';
  const labelColor = theme.palette.text.secondary;

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => window.open(hrefByRole, '_blank', 'noopener,noreferrer')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          window.open(hrefByRole, '_blank', 'noopener,noreferrer');
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={label}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        width: '100%',
        height: { xs: 100, sm: 112, md: 112, lg: 105, xl: 112 },
        boxSizing: 'border-box',
        borderRadius: '16px',
        pt: '12px',
        pb: '12px',
        px: '12px',
        bgcolor: tileBg,
        cursor: 'pointer',
        textDecoration: 'none !important',
        transition: 'background-color 0.15s ease',
        '&, &:hover, &:visited, &:active, &:link, &:focus': {
          textDecoration: 'none !important',
        },
        '&:hover': {
          bgcolor: tileBgHover,
        },
        '&:focus-visible': {
          outline: '2px solid #694AAD',
          outlineOffset: '2px',
        },
      }}
    >
      <Box
        sx={{
          width: iconSize ? `${iconSize}px` : 'clamp(38px, 6vw, 48px)',
          height: iconSize ? `${iconSize}px` : 'clamp(38px, 6vw, 48px)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: circleBg,
          overflow: 'hidden',
        }}
      >
        <img
          src={icon}
          alt=""
          style={{
            width: iconSize ? '100%' : '60%',
            height: iconSize ? '100%' : '60%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <Typography
        component="span"
        sx={{
          width: '100%',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: '0.875rem',
          lineHeight: '140%',
          letterSpacing: '0.17px',
          textAlign: 'center',
          color: `${labelColor} !important`,
          textDecoration: 'none !important',
          borderBottom: 'none !important',
        }}
      >
        {label}
      </Typography>

      {isHovered && hrefByRole && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #1A1A1A',
              mb: '-1px',
            }}
          />
          <Box
            onClick={(e) => {
              e.stopPropagation();
              window.open(hrefByRole, '_blank', 'noopener,noreferrer');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              bgcolor: '#1A1A1A',
              color: '#fff',
              fontSize: '0.7rem',
              px: '8px',
              py: '4px',
              borderRadius: '6px',
              whiteSpace: 'nowrap',
              maxWidth: '240px',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {hrefByRole}
            </Box>
            <OpenInNewIcon sx={{ fontSize: '14px', flexShrink: 0 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default QuickLinkTile;
